/**
 * gs-bundle-builder.js
 * GripSocks mix-and-match bundle builder for the collection page.
 * Injects "Add to Bundle" buttons on product cards, lazy-fetches product data,
 * builds the variant picker modal with option-value button grids, qty stepper,
 * variant image swap, and sold-out disabling.
 *
 * Spec: docs/specs/gs-bundle-builder.md
 * Ported from: SeatShield ss-bundle-builder.js (2026-06-07)
 * Back-ported from F3 f3-bundle-builder.js (2026-06-12): cart-driven
 * persistent tracker, line-key removal, getAttribute tier fix, totals,
 * configurable discount messaging, chip qty steppers, cart→tracker sync.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Config — read from gs-bundle-builder-summary data attrs              */
  /* ------------------------------------------------------------------ */
  var config = {
    tiers: [],
    addBtnText: 'Add to Bundle',
    reviewBtnText: 'Review Bundle',
    emptyText: 'Pick socks to start your bundle.',
    msgUpsell: 'Add [n] more item(s) to get [pct]% off!',
    msgProgress: 'You now have [cur]% off — add [n] more item(s) to get [pct]% off!',
    msgComplete: '🎉 You now have [pct]% off — the best discount!',
    eligibleIds: []
  };

  // Token substitution for the configurable discount messages.
  // [n] = qty gap to next tier, [pct] = next-tier % (current % in msgComplete),
  // [cur] = current discount %.
  function msgTmpl(template, n, pct, cur) {
    return String(template || '')
      .replace(/\[n\]/g, String(n !== undefined ? n : ''))
      .replace(/\[pct\]/g, String(pct !== undefined ? pct : ''))
      .replace(/\[cur\]/g, String(cur !== undefined ? cur : ''));
  }

  var GRID_CARD_SEL = '.grid-product[data-product-handle]';

  /* ------------------------------------------------------------------ */
  /* Bundle state                                                          */
  /* ------------------------------------------------------------------ */
  var bundleItems = []; // [{variantId, productId, title, price, qty}]

  // Optimistic stepper state: lineId -> target qty awaiting /cart/change.js.
  // Rapid +/− clicks coalesce into ONE request with the final quantity, and
  // the target is read from here (not the chip DOM, which lags the server).
  var pendingQty = {};
  var pendingTimers = {};
  function hasPendingLineOps() { return Object.keys(pendingQty).length > 0; }

  // The builder's own drawer refreshes echo back as 'gs:cart:changed' (the
  // drawer dispatches it from updateContents). Suppress that one echo so each
  // tracker action doesn't rebuild the chips twice — the churn was swallowing
  // clicks that landed mid-rebuild.
  var suppressNextCartChanged = false;
  function refreshDrawerQuietly() {
    if (window.GsCartDrawer && typeof window.GsCartDrawer.refresh === 'function') {
      suppressNextCartChanged = true;
      return Promise.resolve(window.GsCartDrawer.refresh());
    }
    return Promise.resolve();
  }

  function totalQty() {
    return bundleItems.reduce(function (s, i) { return s + i.qty; }, 0);
  }

  function activeTier() {
    var qty = totalQty();
    var best = null;
    for (var i = 0; i < config.tiers.length; i++) {
      var t = config.tiers[i];
      if (qty >= t.qty) {
        if (!best || t.qty > best.qty) best = t;
      }
    }
    return best;
  }

  /* ------------------------------------------------------------------ */
  /* Cart-driven tracker (persistent — mirrors MudGear/F3)                 */
  /* The bundle count + chips reflect the ACTUAL cart, filtered to this    */
  /* collection's products (config.eligibleIds). Seeded from /cart.js on   */
  /* load and re-synced after every add/remove/paginate, so the tracker    */
  /* survives reloads and counts socks already in the cart.                */
  /* ------------------------------------------------------------------ */
  function bundleItemsFromCart(cart) {
    var elig = config.eligibleIds;
    return (cart.items || []).filter(function (i) {
      return elig.length === 0 || elig.indexOf(i.product_id) !== -1;
    }).map(function (i) {
      return {
        variantId: i.variant_id,
        productId: i.product_id,
        qty: i.quantity,
        price: i.price,
        original: i.original_line_price,
        final: i.final_line_price,
        title: i.product_title || i.title,
        image: i.image,
        key: i.key
      };
    });
  }

  function syncFromCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Don't clobber optimistic stepper UI while clicks are still pending.
        if (hasPendingLineOps()) return cart;
        bundleItems = bundleItemsFromCart(cart);
        refreshCardButtons();
        updateSummary();
        return cart;
      })
      .catch(function (err) { console.warn('[GsBundleBuilder] cart sync failed:', err); });
  }

  /* ------------------------------------------------------------------ */
  /* Summary bar                                                           */
  /* ------------------------------------------------------------------ */
  function updateSummary() {
    var qty = totalQty();
    var tier = activeTier();
    var countEl   = document.getElementById('GsBundleCount');
    var msgEl     = document.getElementById('GsBundleMsg');
    var reviewBtn = document.getElementById('GsBundleReviewBtn');
    var bar       = document.getElementById('GsBundleSummary');

    if (countEl) countEl.textContent = qty + (qty === 1 ? ' item' : ' items');

    if (qty === 0) {
      if (msgEl) msgEl.textContent = config.emptyText;
      if (bar)   bar.classList.remove('is-active');
    } else {
      if (bar) bar.classList.add('is-active');
      if (msgEl) {
        // Tier-status line: current discount + next milestone, using the
        // configurable section-setting templates (MudGear-style):
        //   no tier yet      → msgUpsell    "Add [n] more item(s) to get [pct]% off!"
        //   tier + next tier → msgProgress  "You now have [cur]% off — add [n] more item(s) to get [pct]% off!"
        //   top tier reached → msgComplete  "🎉 You now have [pct]% off — the best discount!"
        var nextTier = config.tiers.find(function (t) { return t.qty > qty; });
        var need = nextTier ? (nextTier.qty - qty) : 0;
        if (tier && nextTier) {
          msgEl.textContent = msgTmpl(config.msgProgress, need, nextTier.pct, tier.pct);
        } else if (tier) {
          msgEl.textContent = msgTmpl(config.msgComplete, 0, tier.pct, tier.pct);
        } else if (nextTier) {
          msgEl.textContent = msgTmpl(config.msgUpsell, need, nextTier.pct);
        } else {
          msgEl.textContent = qty + (qty === 1 ? ' item' : ' items') + ' in your bundle';
        }
      }
    }

    if (reviewBtn) reviewBtn.disabled = (qty === 0);

    // Price breakdown (subtotal / discount / total) from the eligible cart lines.
    var totalsEl = document.getElementById('GsBundleTotals');
    var subEl    = document.getElementById('GsBundleSubtotal');
    var discRow  = document.getElementById('GsBundleDiscountRow');
    var discEl   = document.getElementById('GsBundleDiscount');
    var totEl    = document.getElementById('GsBundleTotal');
    var subtotal = bundleItems.reduce(function (s, i) { return s + (i.original || 0); }, 0);
    var grand    = bundleItems.reduce(function (s, i) { return s + (i.final || 0); }, 0);
    var discount = subtotal - grand;
    if (qty === 0) {
      if (totalsEl) totalsEl.hidden = true;
    } else {
      if (totalsEl) totalsEl.hidden = false;
      if (subEl) subEl.textContent = formatMoney(subtotal);
      if (totEl) totEl.textContent = formatMoney(grand);
      if (discount > 0) {
        if (discRow) discRow.hidden = false;
        if (discEl)  discEl.textContent = '−' + formatMoney(discount);
      } else if (discRow) {
        discRow.hidden = true;
      }
    }

    renderItems();
  }

  /* ------------------------------------------------------------------ */
  /* Item chips in the summary bar (thumbnail + name + qty + remove)        */
  /* ------------------------------------------------------------------ */
  function shortName(title) {
    if (!title) return 'Item';
    // Drop the "Athletic Socks With Grips - " style prefix; keep the tail
    // (height + color), which is what distinguishes the chips.
    var parts = title.split(' - ');
    return parts.length >= 2 ? parts.slice(1).join(' - ') : title;
  }

  function buildChip(item, chipId) {
    var chip = document.createElement('div');
    chip.className = 'gs-bb-summary__chip';
    chip.dataset.chipId = chipId;
    chip.dataset.variantId = item.variantId;
    // Line item KEY — the only reliable handle for /cart/change.js. Removing
    // by variant id fails (silently) on lines with item properties, e.g.
    // Easify Product Options / TeeInBlue personalizations.
    chip.dataset.lineKey = item.key || '';

    var html = '';
    if (item.image) {
      html += '<img class="gs-bb-summary__chip-img" src="' + escAttr(item.image) + '" alt="" loading="lazy">';
    }
    html += '<span class="gs-bb-summary__chip-name">' + esc(shortName(item.title)) + '</span>';
    html += '<span class="gs-bb-summary__chip-stepper">'
      + '<button type="button" class="gs-bb-summary__chip-step" data-gs-chip-decrement aria-label="Decrease quantity of ' + escAttr(item.title || 'item') + '">−</button>'
      + '<span class="gs-bb-summary__chip-qty" data-gs-chip-qty>' + item.qty + '</span>'
      + '<button type="button" class="gs-bb-summary__chip-step" data-gs-chip-increment aria-label="Increase quantity of ' + escAttr(item.title || 'item') + '">+</button>'
      + '</span>';
    html += '<button type="button" class="gs-bb-summary__chip-remove" data-gs-remove aria-label="Remove ' + escAttr(item.title || 'item') + '">×</button>';
    chip.innerHTML = html;
    return chip;
  }

  function renderItems() {
    // PATCH IN PLACE — never wholesale-rebuild the chip row. Rebuilding
    // destroys the +/− buttons mid-interaction, and a click that lands during
    // the rebuild is silently swallowed. Update existing chips' quantities,
    // append new ones, remove stale ones.
    var itemsEl = document.getElementById('GsBundleItems');
    if (!itemsEl) return;
    var existing = {};
    Array.from(itemsEl.children).forEach(function (ch) {
      existing[ch.dataset.chipId] = ch;
    });
    var seen = {};
    bundleItems.forEach(function (item) {
      var id = String(item.key || item.variantId);
      seen[id] = true;
      var chip = existing[id];
      if (chip) {
        var qtyEl = chip.querySelector('[data-gs-chip-qty]');
        if (qtyEl && qtyEl.textContent !== String(item.qty)) qtyEl.textContent = item.qty;
        chip.style.display = '';
      } else {
        itemsEl.appendChild(buildChip(item, id));
      }
    });
    Object.keys(existing).forEach(function (id) {
      if (!seen[id]) existing[id].remove();
    });
  }

  function changeBundleLine(lineId, qty) {
    // lineId: prefer the line item KEY (string "variantid:hash"); falls back to
    // a numeric variant id. The key targets the exact line even when the line
    // has properties or a selling plan. qty 0 removes the line.
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineId, quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change.js failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        // Rebuild the tracker from the real cart, then refresh the drawer.
        syncFromCart().then(function () {
          refreshDrawerQuietly();
        });
      })
      .catch(function (err) {
        console.warn('[GsBundleBuilder] line change failed:', err);
        // Re-sync anyway so the tracker never shows a stale chip.
        syncFromCart();
      });
  }

  // Coalesce rapid stepper clicks: update local state + UI immediately, then
  // send ONE /cart/change.js with the final quantity after a short quiet
  // period. Target qty is read from pendingQty (not the DOM), so consecutive
  // clicks stay incremental even while the server round-trip is in flight.
  function queueLineChange(chip, lineId, domQty, delta) {
    var base = (pendingQty[lineId] !== undefined) ? pendingQty[lineId] : domQty;
    var target = (delta === null) ? 0 : Math.max(0, base + delta);
    pendingQty[lineId] = target;

    // Optimistic local state → count, discount message, and chip qty update
    // instantly (money totals correct themselves on the post-commit sync).
    var idStr = String(lineId);
    var idx = bundleItems.findIndex(function (i) { return String(i.key || i.variantId) === idStr; });
    if (idx !== -1) {
      if (target === 0) bundleItems.splice(idx, 1);
      else bundleItems[idx].qty = target;
    }
    updateSummary();

    clearTimeout(pendingTimers[lineId]);
    pendingTimers[lineId] = setTimeout(function () {
      var qty = pendingQty[lineId];
      delete pendingQty[lineId];
      delete pendingTimers[lineId];
      changeBundleLine(lineId, qty);
    }, delta === null ? 0 : 350);
  }

  document.addEventListener('click', function (e) {
    var chipBtn = e.target.closest('[data-gs-remove], [data-gs-chip-decrement], [data-gs-chip-increment]');
    if (!chipBtn) return;
    var chip = chipBtn.closest('.gs-bb-summary__chip');
    if (!chip) return;
    var lineKey = chip.dataset.lineKey;
    var variantId = parseInt(chip.dataset.variantId, 10);
    var lineId = lineKey || variantId;
    if (!lineId) return;

    var qtyEl = chip.querySelector('[data-gs-chip-qty]');
    var currentQty = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;

    if (chipBtn.hasAttribute('data-gs-remove')) {
      queueLineChange(chip, lineId, currentQty, null);
    } else if (chipBtn.hasAttribute('data-gs-chip-decrement')) {
      queueLineChange(chip, lineId, currentQty, -1);
    } else {
      queueLineChange(chip, lineId, currentQty, 1);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Cart → tracker sync                                                   */
  /* The cart drawer dispatches 'gs:cart:changed' after every cart         */
  /* mutation it performs (qty change, remove, upsell add, rewards).       */
  /* Re-sync the tracker so it never goes stale. Debounced.                */
  /* ------------------------------------------------------------------ */
  var cartChangedTimer;
  document.addEventListener('gs:cart:changed', function () {
    // Skip the echo from our own drawer refreshes (see refreshDrawerQuietly).
    if (suppressNextCartChanged) { suppressNextCartChanged = false; return; }
    clearTimeout(cartChangedTimer);
    cartChangedTimer = setTimeout(function () { syncFromCart(); }, 100);
  });

  /* ------------------------------------------------------------------ */
  /* Card button state                                                     */
  /* ------------------------------------------------------------------ */
  function isProductInBundle(productId) {
    return bundleItems.some(function (i) { return i.productId === productId; });
  }

  function refreshCardButtons() {
    document.querySelectorAll(GRID_CARD_SEL).forEach(function (card) {
      var productId = parseInt(card.dataset.productId || card.getAttribute('data-product-id'), 10);
      var btn = card.querySelector('.gs-bb-add-btn');
      if (!btn) return;
      var inBundle = isProductInBundle(productId);
      btn.classList.toggle('gs-bb-add-btn--added', inBundle);
      btn.textContent = inBundle ? '✓ Added' : config.addBtnText;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Inject "Add to Bundle" buttons on visible cards                       */
  /* ------------------------------------------------------------------ */
  function injectButtons(scope) {
    var root = scope || document;
    root.querySelectorAll(GRID_CARD_SEL).forEach(function (card) {
      if (card.querySelector('.gs-bb-add-btn')) return;

      var productId = parseInt(card.dataset.productId || card.getAttribute('data-product-id'), 10);
      if (config.eligibleIds.length > 0 && config.eligibleIds.indexOf(productId) === -1) return;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gs-bb-add-btn';
      btn.setAttribute('data-gs-add-btn', '');
      btn.textContent = config.addBtnText;
      btn.dataset.productId = productId;
      btn.dataset.productHandle = card.dataset.productHandle;

      var imgWrap = card.querySelector('.grid-product__image-wrap, .grid-product__content');
      if (imgWrap) {
        imgWrap.style.position = 'relative';
        imgWrap.appendChild(btn);
      } else {
        card.appendChild(btn);
      }
    });
    refreshCardButtons();
  }

  /* ------------------------------------------------------------------ */
  /* Eligible product IDs (Lesson #9 — seeded server-side)               */
  /* ------------------------------------------------------------------ */
  function collectProductIds() {
    document.querySelectorAll('#CollectionAjaxContent .grid-product[data-product-id]').forEach(function (el) {
      var id = parseInt(el.dataset.productId, 10);
      if (id && config.eligibleIds.indexOf(id) === -1) config.eligibleIds.push(id);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Variant cache — lazy-fetches /products/<handle>.js once per handle   */
  /* ------------------------------------------------------------------ */
  var variantCache = {};
  var modalsContainer = null;
  var openModalEl = null;
  var lastFocus = null;

  function openOrFetchModal(handle, triggerBtn) {
    if (variantCache[handle]) {
      openModal(variantCache[handle], triggerBtn);
      return;
    }

    triggerBtn.disabled = true;
    triggerBtn.textContent = 'Loading…';

    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (product) {
        ensureModalsContainer();
        var modal = buildModal(product);
        modalsContainer.appendChild(modal);
        variantCache[handle] = modal;
        triggerBtn.disabled = false;
        triggerBtn.textContent = config.addBtnText;
        openModal(modal, triggerBtn);
      })
      .catch(function (err) {
        console.warn('[GsBundleBuilder] fetch error:', err);
        triggerBtn.disabled = false;
        triggerBtn.textContent = config.addBtnText;
      });
  }

  function ensureModalsContainer() {
    if (modalsContainer) return;
    modalsContainer = document.createElement('div');
    modalsContainer.id = 'gs-bb-modals';
    document.body.appendChild(modalsContainer);
    modalsContainer.addEventListener('click', handleModalClick);
  }

  /* ------------------------------------------------------------------ */
  /* Modal builder — option-value button grid, qty stepper, image swap    */
  /* ------------------------------------------------------------------ */
  function buildModal(product) {
    var firstVariant = product.variants.find(function (v) { return v.available; }) || product.variants[0];

    var variantsData = product.variants.map(function (v) {
      return {
        id: v.id,
        option1: v.option1 || '',
        option2: v.option2 || '',
        option3: v.option3 || '',
        available: v.available,
        price: v.price,
        compare_at_price: v.compare_at_price || 0,
        featured_image: v.featured_image ? (v.featured_image.src || '') : ''
      };
    });

    var hasDefaultVariant = product.variants.length === 1 && product.variants[0].title === 'Default Title';
    var featuredImg = product.featured_image || (product.images && product.images[0]) || '';

    // Build option-value button grid HTML
    var optionsHtml = '';
    if (!hasDefaultVariant && product.options && product.options.length) {
      optionsHtml = '<div class="gs-bundle-builder__modal-options">';
      product.options.forEach(function (optName, optIdx) {
        var optLabel = (optName && typeof optName === 'object') ? (optName.name || '') : optName;
        var vals = getOptionValues(product.variants, optIdx);
        optionsHtml += '<div class="gs-bundle-builder__modal-option" data-gs-modal-option-index="' + optIdx + '">';
        optionsHtml += '<div class="gs-bundle-builder__modal-option-label">' + esc(optLabel) + '</div>';
        optionsHtml += '<div class="gs-bundle-builder__modal-option-values">';
        vals.forEach(function (value) {
          var key = 'option' + (optIdx + 1);
          var isSelected = firstVariant[key] === value;
          // A value is available if ANY variant with that option value is available
          var isAvail = product.variants.some(function (v) { return v[key] === value && v.available; });
          optionsHtml += '<button type="button"'
            + ' class="gs-bundle-builder__modal-option-value' + (isSelected ? ' gs-bundle-builder__modal-option-value--selected' : '') + '"'
            + ' data-gs-modal-option-value'
            + ' data-option-index="' + optIdx + '"'
            + ' data-option-value="' + escAttr(value) + '"'
            + ' aria-pressed="' + (isSelected ? 'true' : 'false') + '"'
            + (isAvail ? '' : ' disabled')
            + '>' + esc(value) + '</button>';
        });
        optionsHtml += '</div></div>';
      });
      optionsHtml += '</div>';
    }

    var compareAtHtml = (firstVariant.compare_at_price && firstVariant.compare_at_price > firstVariant.price)
      ? '<span class="gs-bundle-builder__modal-price-compare" data-gs-modal-price-compare>' + formatMoney(firstVariant.compare_at_price) + '</span>'
      : '<span class="gs-bundle-builder__modal-price-compare" data-gs-modal-price-compare style="display:none"></span>';

    var modal = document.createElement('div');
    modal.className = 'gs-bundle-builder__modal';
    modal.setAttribute('data-gs-modal', '');
    modal.setAttribute('data-product-id', String(product.id));
    modal.setAttribute('data-product-handle', product.handle || '');
    modal.setAttribute('data-variants', JSON.stringify(variantsData));
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'gs-modal-title-' + product.id);
    modal.hidden = true;

    modal.innerHTML = ''
      + '<div class="gs-bundle-builder__modal-backdrop" data-gs-modal-backdrop></div>'
      + '<div class="gs-bundle-builder__modal-panel">'
      +   '<button type="button" class="gs-bundle-builder__modal-close" data-gs-modal-close aria-label="Close">'
      +     '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      +       '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>'
      +     '</svg>'
      +   '</button>'
      +   '<div class="gs-bundle-builder__modal-body">'
      +     '<div class="gs-bundle-builder__modal-image-wrap">'
      +       (featuredImg ? '<img class="gs-bundle-builder__modal-image" data-gs-modal-image src="' + escAttr(featuredImg) + '" alt="' + escAttr(product.title) + '" loading="lazy">' : '')
      +     '</div>'
      +     '<div class="gs-bundle-builder__modal-info">'
      +       '<h2 class="gs-bundle-builder__modal-title" id="gs-modal-title-' + product.id + '">' + esc(product.title) + '</h2>'
      +       '<div class="gs-bundle-builder__modal-price" data-gs-modal-price>'
      +         compareAtHtml
      +         '<span class="gs-bundle-builder__modal-price-current" data-gs-modal-price-current>' + formatMoney(firstVariant.price) + '</span>'
      +       '</div>'
      +       optionsHtml
      +       '<div class="gs-bundle-builder__modal-qty">'
      +         '<span class="gs-bundle-builder__modal-qty-label">Quantity</span>'
      +         '<div class="gs-bundle-builder__modal-qty-stepper">'
      +           '<button type="button" class="gs-bundle-builder__modal-qty-btn" data-gs-modal-qty-decrement aria-label="Decrease quantity">−</button>'
      +           '<input type="number" class="gs-bundle-builder__modal-qty-input" data-gs-modal-qty-input min="1" value="1" inputmode="numeric" aria-label="Quantity">'
      +           '<button type="button" class="gs-bundle-builder__modal-qty-btn" data-gs-modal-qty-increment aria-label="Increase quantity">+</button>'
      +         '</div>'
      +       '</div>'
      +       '<button type="button"'
      +         ' class="gs-bundle-builder__modal-add"'
      +         ' data-gs-modal-add'
      +         ' data-current-variant-id="' + firstVariant.id + '"'
      +         (firstVariant.available ? '' : ' disabled aria-disabled="true"')
      +       '>'
      +         '<span class="gs-bundle-builder__modal-add-label" data-gs-modal-add-label>' + (firstVariant.available ? 'Add to Bundle' : 'Sold out') + '</span>'
      +       '</button>'
      +     '</div>'
      +   '</div>'
      + '</div>';

    return modal;
  }

  /* ------------------------------------------------------------------ */
  /* Modal event delegation on #gs-bb-modals                              */
  /* ------------------------------------------------------------------ */
  function handleModalClick(e) {
    if (e.target.closest('[data-gs-modal-close]') || e.target.matches('[data-gs-modal-backdrop]')) {
      closeModalIfOpen();
      return;
    }

    // Option-value button click → select value + update variant
    var optBtn = e.target.closest('[data-gs-modal-option-value]');
    if (optBtn) {
      var row = optBtn.closest('.gs-bundle-builder__modal-option-values');
      if (row) {
        row.querySelectorAll('[data-gs-modal-option-value]').forEach(function (b) {
          b.classList.remove('gs-bundle-builder__modal-option-value--selected');
          b.setAttribute('aria-pressed', 'false');
        });
        optBtn.classList.add('gs-bundle-builder__modal-option-value--selected');
        optBtn.setAttribute('aria-pressed', 'true');
      }
      updateModalVariantState(optBtn.closest('[data-gs-modal]'));
      return;
    }

    // Qty stepper
    var dec = e.target.closest('[data-gs-modal-qty-decrement]');
    var inc = e.target.closest('[data-gs-modal-qty-increment]');
    if (dec || inc) {
      var modal = (dec || inc).closest('[data-gs-modal]');
      var input = modal.querySelector('[data-gs-modal-qty-input]');
      if (input) {
        var val = parseInt(input.value, 10) || 1;
        if (dec) val = Math.max(1, val - 1);
        if (inc) val++;
        input.value = val;
      }
      return;
    }

    // ADD to Bundle
    var addBtn = e.target.closest('[data-gs-modal-add]');
    if (!addBtn || addBtn.disabled) return;

    var addModal = addBtn.closest('[data-gs-modal]');
    var variantId = parseInt(addBtn.dataset.currentVariantId, 10);
    var qtyInput  = addModal.querySelector('[data-gs-modal-qty-input]');
    var qty       = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;
    var productId = parseInt(addModal.dataset.productId, 10);
    if (!variantId) return;

    // Retrieve cached product title from variantCache
    var handle = addModal.dataset.productHandle;
    var variants = [];
    try { variants = JSON.parse(addModal.dataset.variants || '[]'); } catch (e) { variants = []; }
    var variant = variants.find(function (v) { return v.id === variantId; });

    addBtn.disabled = true;
    var label = addModal.querySelector('[data-gs-modal-add-label]');
    var prevLabel = label ? label.textContent : '';
    if (label) label.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add.js failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        closeModalIfOpen();
        // Rebuild the tracker from the real cart, then refresh the drawer.
        syncFromCart().then(function () {
          refreshDrawerQuietly();
        });
        addBtn.disabled = false;
        if (label) label.textContent = prevLabel || 'Add to Bundle';
      })
      .catch(function (err) {
        console.warn('[GsBundleBuilder] addToCart error:', err);
        if (label) label.textContent = prevLabel || 'Add to Bundle';
        addBtn.disabled = false;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Modal open / close                                                    */
  /* ------------------------------------------------------------------ */
  function openModal(modal, trigger) {
    closeModalIfOpen();
    lastFocus = trigger || document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    openModalEl = modal;
    var qtyInput = modal.querySelector('[data-gs-modal-qty-input]');
    if (qtyInput) qtyInput.value = 1;
    updateModalVariantState(modal);
    var closeBtn = modal.querySelector('[data-gs-modal-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModalIfOpen() {
    if (!openModalEl) return;
    openModalEl.hidden = true;
    document.body.style.overflow = '';
    openModalEl = null;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  /* ------------------------------------------------------------------ */
  /* Variant state — image swap, price, add-btn availability              */
  /* ------------------------------------------------------------------ */
  function updateModalVariantState(modal) {
    if (!modal) return;
    var variants;
    try { variants = JSON.parse(modal.dataset.variants || '[]'); } catch (e) { variants = []; }

    // Build selected options array from button state
    var selected = [];
    modal.querySelectorAll('[data-gs-modal-option-value].gs-bundle-builder__modal-option-value--selected').forEach(function (b) {
      var idx = parseInt(b.dataset.optionIndex, 10);
      if (!isNaN(idx)) selected[idx] = b.dataset.optionValue;
    });

    var matched = variants.find(function (v) {
      if (selected[0] !== undefined && v.option1 !== selected[0]) return false;
      if (selected[1] !== undefined && v.option2 !== selected[1]) return false;
      if (selected[2] !== undefined && v.option3 !== selected[2]) return false;
      return true;
    });

    var addBtn    = modal.querySelector('[data-gs-modal-add]');
    var addLabel  = modal.querySelector('[data-gs-modal-add-label]');
    var priceCur  = modal.querySelector('[data-gs-modal-price-current]');
    var priceCmp  = modal.querySelector('[data-gs-modal-price-compare]');
    var modalImg  = modal.querySelector('[data-gs-modal-image]');

    if (matched) {
      addBtn.dataset.currentVariantId = matched.id;
      if (matched.available) {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
        if (addLabel) addLabel.textContent = 'Add to Bundle';
      } else {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        if (addLabel) addLabel.textContent = 'Sold out';
      }

      // Variant image swap
      if (modalImg && matched.featured_image) modalImg.src = matched.featured_image;

      if (priceCur) priceCur.textContent = formatMoney(matched.price);
      if (priceCmp) {
        if (matched.compare_at_price && matched.compare_at_price > matched.price) {
          priceCmp.textContent = formatMoney(matched.compare_at_price);
          priceCmp.style.display = '';
        } else {
          priceCmp.style.display = 'none';
        }
      }
    } else if (addBtn) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      if (addLabel) addLabel.textContent = 'Unavailable';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Option value helpers                                                  */
  /* ------------------------------------------------------------------ */
  function getOptionValues(variants, optIdx) {
    var seen = new Set();
    var vals = [];
    variants.forEach(function (v) {
      var val = v['option' + (optIdx + 1)];
      if (val && !seen.has(val)) { seen.add(val); vals.push(val); }
    });
    return vals;
  }

  /* ------------------------------------------------------------------ */
  /* Event delegation — "Add to Bundle" card button clicks                */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-gs-add-btn]');
    if (!btn) return;
    e.preventDefault();

    var handle = btn.dataset.productHandle;
    var productId = parseInt(btn.dataset.productId, 10);
    if (!handle) return;

    openOrFetchModal(handle, btn);
  });

  // ESC closes modal
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openModalEl) closeModalIfOpen();
  });

  // Review Bundle button → refresh the cart drawer, THEN open it.
  // The bundle items are already in the cart (each "Add to Bundle" posts to
  // /cart/add.js), but the drawer may be showing a stale snapshot from racing
  // per-add refreshes — so re-render it via the Section Rendering API and only
  // open once that resolves, guaranteeing the freshly added socks are shown.
  document.addEventListener('click', function (e) {
    if (!e.target.closest('#GsBundleReviewBtn')) return;
    if (!window.GsCartDrawer) return;
    refreshDrawerQuietly().then(function () {
      if (typeof window.GsCartDrawer.open === 'function') window.GsCartDrawer.open();
    });
  });

  /* ------------------------------------------------------------------ */
  /* MutationObserver — re-inject buttons after AJAX pagination/filter    */
  /* ------------------------------------------------------------------ */
  function observeAjaxGrid() {
    var grid = document.getElementById('CollectionAjaxContent');
    if (!grid) return;
    var debounceTimer;
    new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        collectProductIds();
        injectButtons();
        syncFromCart();  // newly loaded pages may include eligible cart items
      }, 120);
    }).observe(grid, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /* String escaping (for JS-built modal HTML)                            */
  /* ------------------------------------------------------------------ */
  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escAttr(s) {
    return String(s || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ------------------------------------------------------------------ */
  /* Money formatter                                                       */
  /* ------------------------------------------------------------------ */
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        var fmt = (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                  */
  /* ------------------------------------------------------------------ */
  function init() {
    var summaryEl = document.getElementById('GsBundleSummary');
    if (!summaryEl) return;

    config.addBtnText    = summaryEl.dataset.addBtnText    || 'Add to Bundle';
    config.reviewBtnText = summaryEl.dataset.reviewBtnText || 'Review Bundle';
    config.emptyText     = summaryEl.dataset.emptyText     || 'Pick socks to start your bundle.';
    config.msgUpsell     = summaryEl.getAttribute('data-msg-upsell')   || config.msgUpsell;
    config.msgProgress   = summaryEl.getAttribute('data-msg-progress') || config.msgProgress;
    config.msgComplete   = summaryEl.getAttribute('data-msg-complete') || config.msgComplete;

    config.tiers = [];
    for (var i = 1; i <= 3; i++) {
      // getAttribute, NOT dataset: the camelCase algorithm only folds hyphens
      // before LETTERS, so data-tier-qty-1 maps to dataset['tierQty-1'] — a
      // dataset['tierQty1'] read silently returns undefined and the tier
      // config ends up empty (no status messages, no milestone math).
      var qty = parseInt(summaryEl.getAttribute('data-tier-qty-' + i), 10);
      var pct = parseInt(summaryEl.getAttribute('data-tier-discount-pct-' + i), 10);
      if (qty > 0 && pct > 0) config.tiers.push({ qty: qty, pct: pct });
    }
    config.tiers.sort(function (a, b) { return a.qty - b.qty; });

    config.eligibleIds = (window.GsBundleEligibleIds || []).slice();

    collectProductIds();
    injectButtons();
    updateSummary();   // instant empty baseline
    syncFromCart();    // then seed the tracker from the actual cart (persistent)
    observeAjaxGrid();
  }

  init();

})();
